import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(identifier.trim(), password);
      if (u.role !== "admin" && u.role !== "super_admin") {
        setError("This account does not have admin access.");
        return;
      }
      toast.success("Welcome, Admin");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-10"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #0b1a14 0%, #050808 55%, #000 100%)",
      }}
    >
      <div
        data-testid="admin-login-card"
        className="w-full max-w-md rounded-2xl p-8 sm:p-10 border border-emerald-400/10 shadow-[0_20px_80px_-30px_rgba(16,185,129,0.35)]"
        style={{
          background:
            "linear-gradient(180deg, rgba(24,24,27,0.95) 0%, rgba(9,9,11,0.95) 100%)",
        }}
      >
        <div className="flex flex-col items-center text-center">
          <div
            className="h-14 w-14 rounded-md bg-white flex items-center justify-center border border-emerald-400/30 shadow p-0.5"
          >
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_ref-connect-app/artifacts/02ztij1f_logo.png"
              alt="Unique Deal India"
              className="h-full w-full object-contain"
            />
          </div>
          <h1
            className="mt-4 text-3xl font-extrabold text-emerald-400 tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Admin Login
          </h1>
          <p className="mt-1 text-sm text-white/70">Access the admin control panel</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2">
              Admin User ID
            </label>
            <input
              data-testid="admin-identifier-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Enter admin user ID"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:ring-2 focus:ring-emerald-400/60 focus:border-transparent outline-none transition-all"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                data-testid="admin-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 pr-12 text-white placeholder-white/40 focus:ring-2 focus:ring-emerald-400/60 focus:border-transparent outline-none transition-all"
                required
              />
              <button
                type="button"
                data-testid="admin-toggle-password"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="text-sm text-red-300 bg-red-500/10 border border-red-400/20 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-emerald-400 text-emerald-400 hover:text-black hover:bg-emerald-400 font-bold py-3 transition-colors active:scale-[0.99] disabled:opacity-60"
          >
            <ShieldCheck size={18} />
            {loading ? "Signing in..." : "Login as Admin"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            data-testid="back-user-login-link"
            to="/login"
            className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Back to User Login
          </Link>
        </div>
      </div>
    </div>
  );
}
